/**
 * Utilidad para consultar tipos de cambio BCU desde Firestore.
 *
 * Los documentos en exchange_rates tienen ID "YYYY-MM-DD" y campos:
 *   date, usd_buy, usd_sell, ui_value, ur_value, source, fetchedAt, carriedFrom?
 */

import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useState, useEffect } from "react";

/**
 * Formatea una fecha JS a "YYYY-MM-DD".
 */
function formatDateYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Obtiene el tipo de cambio para una fecha dada.
 * Si no existe, busca hacia atras hasta 7 dias.
 * @param {Date} date
 * @returns {Promise<{ date: string, buy: number, sell: number, ui: number, ur: number, carriedFrom?: string } | null>}
 */
export async function getExchangeRate(date) {
  try {
    for (let i = 0; i < 7; i++) {
      const d = new Date(date);
      d.setDate(d.getDate() - i);
      const dateStr = formatDateYMD(d);

      const snap = await getDoc(doc(db, "exchange_rates", dateStr));
      if (snap.exists()) {
        const data = snap.data();
        return {
          date: data.date,
          buy: data.usd_buy,
          sell: data.usd_sell,
          ui: data.ui_value || null,
          ur: data.ur_value || null,
          carriedFrom: data.carriedFrom || null,
        };
      }
    }
  } catch (err) {
    console.warn("Error consultando TC BCU:", err.message);
  }
  return null;
}

/**
 * Obtiene el TC del dia anterior a una fecha dada.
 * Util para conversiones al cierre: se usa el TC del dia anterior a la fecha de uso.
 * @param {string} dateStr - "YYYY-MM-DD" (fecha de uso del amenity)
 */
export async function getRateForPreviousDay(dateStr) {
  const useDate = new Date(dateStr + "T00:00:00");
  useDate.setDate(useDate.getDate() - 1);
  return getExchangeRate(useDate);
}

/**
 * Obtiene el TC del dia anterior (cierre mas reciente desde hoy).
 */
export async function getPreviousDayRate() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return getExchangeRate(yesterday);
}

/**
 * Obtiene el TC mas reciente disponible.
 */
export async function getLatestRate() {
  return getExchangeRate(new Date());
}

/**
 * Hook React que retorna el TC mas reciente (USD, UI y UR).
 * Nunca lanza errores — retorna null si no hay datos.
 */
export function useLatestRate() {
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getLatestRate()
      .then(r => { if (!cancelled) setRate(r); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { rate, loading };
}
